// The calendar's Export… dialog (spec/dashboard.md section 8.5; spec/api-conventions.md section 21.7): the range (the shared
// analytics range by default, at most 92 days), the event names (empty = every name), Include testers → POST
// …/envs/{env}/analytics:export → the analytics.export job followed with JobProgress, then the download link.
import { useState } from 'react';
import { ops } from '../../api/operations';
import type { Job } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ExportJob } from '../../content/exports';
import { useLiveOpsEnv } from '../../content/liveops/env';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Checkbox, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { rangeError, useAnalyticsRange } from '../../workspace/analyticsRange';

const NAME = /^((session|profile|level|liveops|payments|ads)\.[a-z][a-z0-9_]*|custom\.[a-z][a-z0-9_]{0,63})$/;

export function AnalyticsExportDialog({ env, onClose }: { env: string; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, can } = useGame();
  const toasts = useToasts();
  const { env: figuresEnv } = useLiveOpsEnv();
  const { range } = useAnalyticsRange(figuresEnv);
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [names, setNames] = useState('');
  const [testers, setTesters] = useState(range.includeTesters);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const list = names
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const badName = list.find((x) => !NAME.test(x));
  const rangeProblem = rangeError(from, to);
  const permission = can('analytics_read', env) ? null : 'analytics_read';
  const valid = !rangeProblem && !badName && !permission;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const queued = await client.data(ops.exportAnalytics, { path: { org, game: game.slug, env }, body: { from, to, names: list.length ? list : null, includeTesters: testers }, idempotencyKey });
      setJob(queued as Job);
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Export analytics · ${env}`}
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>{job ? 'Close' : 'Cancel'}</Button>
          {!job && (
            <Button variant="primary" onClick={() => void submit()} disabled={!valid} busy={busy} missingPermission={permission}>
              Export
            </Button>
          )}
        </>
      }
    >
      <div className="lo-export" data-testid="analytics-export-dialog">
        <p className="lo-export-lede">The raw analytics events of the game-time days, one JSON object per line (gzip NDJSON), no row cap; the file is kept 7 days.</p>
        {job ? (
          <section className="pane" aria-label="Export job">
            <div className="pane-head">
              <h3>Export job</h3>
              <span className="lo-meta">
                {from} → {to}
                {list.length ? ` · ${list.length} event name${list.length === 1 ? '' : 's'}` : ' · every name'}
                {testers ? ' · testers included' : ''}
              </span>
            </div>
            <div className="pane-body">
              <ExportJob job={job} filename={`analytics-${env}-${from}-${to}.ndjson.gz`} permission={permission} />
            </div>
          </section>
        ) : (
          <div className="form-grid">
            <Field label="From" error={rangeProblem ?? undefined}>
              <input type="date" className="input" aria-label="Export from" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input type="date" className="input" aria-label="Export to" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Field className="span-2" label="Event names" help="Comma- or space-separated (session.started, custom.level_up …); empty exports every name." error={badName ? `"${badName}" is not an event name.` : undefined}>
              <TextInput aria-label="Event names" value={names} onChange={(e) => setNames(e.target.value)} placeholder="every name" />
            </Field>
            <div className="span-2">
              <Checkbox label="Include testers" checked={testers} onChange={(e) => setTesters(e.target.checked)} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
